import type { LaneId, PlayerObservationV1, VisibleCardViewV1 } from "@fate-kingdom/engine";
import type { RefCallback } from "react";

import type { AlphaBattlefieldCameraState } from "../camera/battlefield-camera-types";
import type { AlphaAttackAction } from "../drag/useAlphaFormationDrag";
import type { AlphaDisbandAction } from "../drag/useAlphaFieldDisbandDrag";
import { AttackPathOverlay, type AlphaAttackPreview } from "./AttackPathOverlay";
import { BattlefieldLane } from "./BattlefieldLane";
import { CombatPresentation, type AlphaCombatPresentationState } from "./CombatPresentation";

export function PortraitBattlefield({
  observation,
  camera,
  legalTargetIds,
  hoveredLaneId,
  targetMode,
  dragging,
  placementPulseLaneId,
  attackActionsBySource,
  disbandActionsByLane,
  selectedAttackSourceLaneId,
  attackPreview,
  combat,
  interactionBlocked,
  laneRef,
  dragLabelByLane,
  onChooseLane,
  onSelectAttack,
  onFormationPointerDown,
  onInspectCards,
}: {
  readonly observation: PlayerObservationV1;
  readonly camera: AlphaBattlefieldCameraState;
  readonly legalTargetIds: readonly LaneId[];
  readonly hoveredLaneId?: LaneId | undefined;
  readonly targetMode: "own" | "opponent";
  readonly dragging: boolean;
  readonly placementPulseLaneId?: LaneId | undefined;
  readonly attackActionsBySource: ReadonlyMap<LaneId, ReadonlyMap<LaneId, AlphaAttackAction>>;
  readonly disbandActionsByLane: ReadonlyMap<LaneId, ReadonlyMap<string, AlphaDisbandAction>>;
  readonly selectedAttackSourceLaneId?: LaneId | undefined;
  readonly attackPreview?: AlphaAttackPreview | undefined;
  readonly combat: AlphaCombatPresentationState | null;
  readonly interactionBlocked: boolean;
  readonly laneRef: (laneId: LaneId) => RefCallback<HTMLButtonElement>;
  readonly dragLabelByLane: ReadonlyMap<LaneId, string>;
  readonly onChooseLane: (laneId: LaneId) => void;
  readonly onSelectAttack: (laneId: LaneId) => void;
  readonly onFormationPointerDown: (
    laneId: LaneId,
    actions: ReadonlyMap<LaneId, AlphaAttackAction>,
    disbandActionsByCardId: ReadonlyMap<string, AlphaDisbandAction>,
  ) => React.PointerEventHandler<HTMLButtonElement>;
  readonly onInspectCards: (cards: readonly VisibleCardViewV1[]) => void;
}) {
  const laneIds = ["left", "center", "right"] as const;
  return (
    <section
      className={`alpha-war-table is-camera-${camera.mode}`}
      data-alpha-camera-mode={camera.mode}
      data-alpha-camera-snap={camera.snap}
      data-alpha-camera-pan-x={camera.panX.toFixed(3)}
      data-alpha-camera-pan-y={camera.panY.toFixed(3)}
      data-alpha-camera-zoom={camera.zoom.toFixed(3)}
      aria-label="Three-lane portrait battlefield"
      style={{ "--alpha-camera-pan": camera.pan } as React.CSSProperties}
    >
      <div className="alpha-war-table__lanes">
        {laneIds.map((laneId) => {
          const legal = legalTargetIds.includes(laneId);
          const hovered = hoveredLaneId === laneId;
          const dropState = !dragging
            ? "idle"
            : hovered && legal
              ? "hovered"
              : legal
                ? "legal"
                : "invalid";
          const attackActions = attackActionsBySource.get(laneId);
          const disbandActionsByCardId = disbandActionsByLane.get(laneId) ?? new Map();
          return (
            <BattlefieldLane
              laneId={laneId}
              lane={observation.lanes[laneId]}
              perspectivePlayerId={observation.perspectivePlayerId}
              laneRef={laneRef(laneId)}
              targetMode={targetMode}
              legal={legal}
              hovered={hovered}
              dropState={dropState}
              placementPulse={placementPulseLaneId === laneId}
              disabled={interactionBlocked || dragging}
              dragLabel={dragLabelByLane.get(laneId)}
              attackActions={attackActions}
              disbandActionsByCardId={disbandActionsByCardId}
              attackSelected={selectedAttackSourceLaneId === laneId}
              onChooseLane={() => onChooseLane(laneId)}
              onSelectAttack={() => onSelectAttack(laneId)}
              onFormationPointerDown={
                attackActions === undefined && disbandActionsByCardId.size === 0
                  ? undefined
                  : onFormationPointerDown(
                      laneId,
                      attackActions ?? new Map(),
                      disbandActionsByCardId,
                    )
              }
              onInspectCards={onInspectCards}
              key={laneId}
            />
          );
        })}
      </div>
      <AttackPathOverlay
        sourceLaneId={selectedAttackSourceLaneId}
        targetLaneIds={
          selectedAttackSourceLaneId === undefined
            ? []
            : [...(attackActionsBySource.get(selectedAttackSourceLaneId)?.keys() ?? [])]
        }
        preview={attackPreview}
      />
      <CombatPresentation combat={combat} />
    </section>
  );
}
